import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Calendar, Share2, Plane, TrendingUp, Ship, TramFront, Globe, Truck, Snowflake, Bot, Zap, Users, Building2, AlertTriangle, FileText, Package, Anchor, Shield, TrendingDown, Train, Fuel, Brain, Leaf } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

// Function to create article URL

const articles = [
  {
    id: 1,
    title: "Amazon Announces New Distribution Centre in France with €250M Investment, Creating 2,000 Jobs",
    description: "Amazon announces construction of a new 189,000 m² distribution centre in Ensisheim, Haut-Rhin (Grand Est), set to open by end of 2027 with a €250M investment, creating 2,000 new jobs. Amazon has invested over €1.5 billion in Grand Est since 2010 and employs 4,000+ permanent staff in the region.",
    image: "https://www.capital.fr/imgre/fit/~1~cap~2026~04~15~6ed3127c-8f31-4e34-a6e5-a9705b8edbd7.jpeg/630x354/quality/63/crop-from/center/focus-point/50%2C50/image.avif",
    category: "Logistics Investment",
    icon: Package,
    color: "rgb(59,130,246)",
    date: "April 2026",
    stats: [
      { label: "Investment", value: "€250M", color: "text-blue-600" },
      { label: "New Jobs", value: "2,000", color: "text-green-600" },
      { label: "Floor Space", value: "189,000 m²", color: "text-purple-600" }
    ]
  },
  {
    id: 2,
    title: "European Data Centres to Drive Surge in Logistics Space Demand Over Next Three Years",
    description: "Savills research reveals European data centre expansion will generate 8.5 million sq ft (786,130 sqm) of additional logistics space demand across FLAPD markets, with 950 MW under construction. DC supply chain needs dedicated warehousing for servers, power modules and networking systems, following DHL's US model of 10 dedicated sites totalling 7 million sq ft.",
    image: "https://media.datacenterdynamics.com/media/images/GettyImages-11837461681.width-358.jpg",
    category: "Data Centre & Logistics",
    icon: Building2,
    color: "rgb(139,92,246)",
    date: "April 2026",
    stats: [
      { label: "Logistics Demand", value: "8.5M sq ft", color: "text-purple-600" },
      { label: "DC Under Construction", value: "950 MW", color: "text-blue-600" },
      { label: "Space per MW", value: "8,900 sq ft", color: "text-green-600" }
    ]
  },
  {
    id: 3,
    title: "€1bn Prologis-La Caisse Joint Venture PLIVE Launches Pan-European Logistics Platform Across 5 Countries",
    description: "Prologis and La Caisse (CDPQ) launch PLIVE — Prologis Logistics Investment Venture Europe — a €1bn (CAD 1.6bn) pan-European logistics joint venture covering 844,000 sqm of Class A space across France, Germany, Netherlands, Sweden and UK. La Caisse holds 70%, Prologis 30% as operating partner. Closing expected Q2 2026.",
    image: "https://cdn-trans.info/uploads/2026/04/2041fd8cfc61d0090fdcf37833b1.webp",
    category: "Logistics Investment",
    icon: Building2,
    color: "rgb(16,185,129)",
    date: "April 2026",
    stats: [
      { label: "Seed Assets", value: "€1bn", color: "text-green-600" },
      { label: "Class A Space", value: "844,000 sqm", color: "text-blue-600" },
      { label: "Countries", value: "5", color: "text-emerald-600" }
    ]
  },
  {
    id: 4,
    title: "Air Cargo Momentum Torpedoed by Middle East Conflict: Volumes +11.2% in Feb Before War Sent Dubai Rates Up 152%",
    description: "Global air freight saw strong February 2026 momentum with volumes +11.2% YoY and load factor up to 46%, but the Middle East conflict that erupted on 28 February has disorganised flows, sent kerosene prices to record highs, and driven Dubai-to-US spot rates up 152% YoY to $10.33/kg. IATA's Willie Walsh warns of major shifts ahead.",
    image: "https://market-insights.upply.com/hs-fs/hubfs/market-insights/aerien/palette-avion_tarmac_iStock-2200360493.jpeg?width=730&height=395&name=palette-avion_tarmac_iStock-2200360493.jpeg",
    category: "Air Cargo",
    icon: Plane,
    color: "rgb(6,182,212)",
    date: "April 2026",
    stats: [
      { label: "Feb Volumes", value: "+11.2% YoY", color: "text-green-600" },
      { label: "Dubai–US Rate", value: "+152% YoY", color: "text-red-600" },
      { label: "ME Capacity", value: "-33% YoY", color: "text-orange-600" }
    ]
  },
  {
    id: 5,
    title: "Amazon Introduces 1.5% Transport Surcharge for European FBA Partners Across 10 Countries Due to Middle East War",
    description: "Amazon will add a 1.5% fuel and logistics surcharge to FBA shipping costs from April 17 in 10 European countries (DE, UK, FR, IT, ES, PL, SE, NL, IE, BE), and MCF from May 2 in 5 major markets. The US and Canada face a higher 3.5% surcharge. Amazon says the fee is temporary and \"significantly lower than other major carriers\".",
    image: "https://ecommercenews.eu/static/wp-content/uploads/2023/08/Amazon-low-price-FBA-rates-.jpg?w=360&h=240&bg=white&fm=webp",
    category: "E-commerce",
    icon: Globe,
    color: "rgb(245,158,11)",
    date: "April 2026",
    stats: [
      { label: "FBA Surcharge (EU)", value: "1.5%", color: "text-orange-600" },
      { label: "US/Canada Surcharge", value: "3.5%", color: "text-red-600" },
      { label: "Countries Affected", value: "10 (FBA)", color: "text-blue-600" }
    ]
  },
  {
    id: 6,
    title: "IRU 2026 Report: EU Diesel Prices Surge 20% After Iran War, Hauliers Face Mounting Cost Pressure from Fuel, ETS2 and Driver Shortages",
    description: "IRU's 2026 road freight briefing warns that EU diesel prices surged 20% after the February 28 Iran war outbreak, with Ireland hitting €2.3/litre. Combined with ETS2 (€50–100/tonne CO2), Eurovignette expansion, a 12.1% driver shortage and 6% drop in new truck registrations, EU hauliers face a multi-front cost crisis — even as total road freight reached 2.08 trillion tonne-km in 2024.",
    image: "https://cdn-trans.info/uploads/2025/07/bfd1ffbbb50e1b2f1351bda8bf6.webp",
    category: "Road Freight",
    icon: Truck,
    color: "rgb(220,38,38)",
    date: "April 2026",
    stats: [
      { label: "EU Diesel Surge", value: "+20% (post-war)", color: "text-red-600" },
      { label: "Driver Shortage", value: "12.1% unfilled", color: "text-orange-600" },
      { label: "Road Freight 2024", value: "2.08T tkm", color: "text-blue-600" }
    ]
  },
  {
    id: 7,
    title: "ELO Mandatory from 20 April 2026: All UK-France Smart Border Trucks Must Present Obligatory Logistics Envelope or Risk Being Turned Away",
    description: "French Customs confirms that from 20 April 2026, all trucks — loaded or empty — crossing the France-UK Smart Border via Calais, Dunkirk or the Channel Tunnel must present an ELO (Obligatory Logistics Envelope) barcode at check-in. Missing ELO may result in refused boarding or unprocessed customs formalities. The system covers imports, exports and transits. One truck = one ELO.",
    image: "https://cdn-trans.info/uploads/2025/10/33ca8cb471880b7a6c4e0d81896.webp",
    category: "Border Compliance",
    icon: FileText,
    color: "rgb(99,102,241)",
    date: "April 2026",
    stats: [
      { label: "Deadline", value: "20 April 2026", color: "text-red-600" },
      { label: "Applies To", value: "All trucks (loaded/empty)", color: "text-orange-600" },
      { label: "Key Corridors", value: "Calais / Dunkirk / Tunnel", color: "text-indigo-600" }
    ]
  },
  {
    id: 8,
    title: "Betz International Files for Insolvency: 140 Jobs at Risk as Rising Diesel Costs, Margin Pressure and Weak German Industry Hit Mid-Sized Haulier",
    description: "Preliminary insolvency proceedings were opened for Betz International on 7 April 2026 at Tübingen court. The Willi Betz Group subsidiary, founded in 1945, employs 140 staff whose wages are secured for 3 months. Causes: soaring diesel costs, price pressure, structurally low road freight margins and weak German industrial output. Part of a wider EU mid-market consolidation wave also hitting Austria's Nothegger (300 staff) and others.",
    image: "https://cdn-trans.info/uploads/2026/04/f4cb8e4bb66cba9eb4ca784dc09-en.webp",
    category: "Market Consolidation",
    icon: AlertTriangle,
    color: "rgb(239,68,68)",
    date: "April 2026",
    stats: [
      { label: "Employees at Risk", value: "140", color: "text-red-600" },
      { label: "Founded", value: "1945 (81 yrs)", color: "text-orange-600" },
      { label: "Nothegger (AT)", value: "300 staff / €15M+", color: "text-yellow-600" }
    ]
  },
  {
    id: 9,
    title: "CEVA Logistics Adds 165 New DAF Trucks to UK Fleet Running on HVO Fuel with Up to 90% Less CO₂, Fitted with Telematics",
    description: "CEVA Logistics has expanded its UK road transport fleet with 165 new DAF trucks, operating predominantly on hydrotreated vegetable oil (HVO) which can reduce CO₂ emissions by up to 90% vs conventional diesel. All vehicles are fitted with telematics for real-time route and safety data. The fleet was introduced in partnership with Ford & Slater and leasing provider TIP Group.",
    image: "https://cdn-trans.info/uploads/2026/04/e821964380d1bd9a37e6954c54b.webp",
    category: "Fleet & Green Tech",
    icon: Leaf,
    color: "rgb(34,197,94)",
    date: "April 2026",
    stats: [
      { label: "New Trucks", value: "165 DAF", color: "text-green-600" },
      { label: "CO₂ Reduction", value: "Up to 90%", color: "text-emerald-600" },
      { label: "Fuel", value: "HVO (vs diesel)", color: "text-blue-600" }
    ]
  }
];



const Index = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8 space-y-12">
        {/* Header Section */}
        <section id="industry-highlights">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">{t('header.title')}</h2>
            </div>
            <LanguageSwitcher />
          </div>


          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.filter(article => article.id <= 9).map((article) => {
              const IconComponent = article.icon;
              return (
                <Link key={article.id} to={`/article/${article.id}`}>
                  <Card className="hover:shadow-lg transition-shadow">
                    {/* Image Section */}
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img 
                        src={article.image} 
                        alt={t(`article.${article.id}.title`)}
                        className="w-full h-[192px] object-cover"
                      />
                      <Badge 
                        className="absolute top-4 left-4 text-white border-transparent"
                        style={{ backgroundColor: article.color }}
                      >
                        <IconComponent className="h-3 w-3 mr-1" />
                        {article.category}
                      </Badge>
                    </div>

                    {/* Content Section */}
                    <CardHeader>
                      <CardTitle className="text-lg">{t(`article.${article.id}.title`)}</CardTitle>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-1" />
                        {article.date}
                      </div>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-600 mb-4">{t(`article.${article.id}.description`)}</p>
                      
                      {/* Stats */}
                      <div className="space-y-2 mb-4">
                        {article.stats.map((stat, index) => (
                          <div key={index} className="flex justify-between">
                            <span className="text-sm text-gray-600">{stat.label}:</span>
                            <span className={`font-semibold ${stat.color}`}>{stat.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-between items-center">
                        <Badge variant="secondary">{article.category}</Badge>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">{t('common.readMore')}</Button>
                          <Button variant="outline" size="sm" className="ml-auto">
                            <Share2 className="h-4 w-4 mr-2" />
                            {t('common.share')}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <Separator />
        <footer>
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div>
                <h3 className="text-xl font-bold mb-2">{t('footer.title')}</h3>
                <p className="text-blue-200">{t('footer.subtitle')}</p>
                <p className="text-blue-200 text-sm">{t('footer.lastUpdated')}</p>
              </div>
              <div className="mt-4 md:mt-0">
                {/* Additional footer content can go here */}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;